import * as React from "react";
import "./catalogue-pagination.scss";

export interface CataloguePaginationData {
  offset: number;
  limit: number;
  total: number;
}

export interface CataloguePaginationProps {
  data: CataloguePaginationData;
  onClick: (data: CataloguePaginationData) => void;
  pageNeighborsCutOff?: number;
}

interface PageLinkProps {
  pageNumber: number;
  className?: string;
  title: string;
  content: string | number;
  onClick: (e: any, pageNumber: number) => void;
}
const PageLink: React.FC<PageLinkProps> = ({ pageNumber, className, title, content, onClick }) => (
  <li>
    <a className={className} title={title} onClick={e => onClick(e, pageNumber)} href={`?p=${pageNumber}`}>
      {content}
    </a>
  </li>
);

export const CataloguePagination: React.FC<CataloguePaginationProps> = ({
  data: { offset, limit, total },
  onClick,
  pageNeighborsCutOff
}) => {
  const cutOff = pageNeighborsCutOff || 2;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const pageLinkNumbersToDisplay = (): number[] => {
    const pagesResult = Array.from({ length: totalPages }).map((_, i) => i + 1);

    // If there is a limited number of pages we return early
    if (totalPages < cutOff * 2) {
      return pagesResult;
    }

    const currentPageIndex = currentPage - 1;
    const prevAndNextOffset = cutOff * 2 + 1;
    const lastPageIndex = totalPages;

    // Before cutoff case, i.e. page 1
    if (currentPageIndex < cutOff) {
      return pagesResult.slice(0, prevAndNextOffset);
    }

    // After cutoff cases, i.e. page n - 1
    if (currentPageIndex > lastPageIndex - cutOff) {
      return pagesResult.slice(prevAndNextOffset * -1);
    }

    // Cases with n pages > cutOff on either side
    return pagesResult.slice(currentPageIndex - cutOff, currentPageIndex + cutOff + 1);
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>, targetPage: number) => {
    e.preventDefault();
    onClick({
      limit,
      offset: limit * targetPage - limit,
      total
    });
  };

  const usePagination = limit < total;
  const hasPrevious = offset > 0;
  const hasNext = offset < total - limit;

  const firstItemIndex = offset + 1;
  const lastItemIndex = firstItemIndex + limit - 1;

  const pageLinkNumbers = pageLinkNumbersToDisplay();

  return (
    <div className="c-pager">
      {usePagination ? (
        <>
          <p className="c-pager__shown-total" data-testid="item-indices-of-total">
            {firstItemIndex}-{lastItemIndex} of {total}
          </p>
          <div className="c-pager__pages">
            <ol>
              {hasPrevious && (
                <PageLink
                  title="Previous Page"
                  pageNumber={currentPage - 1}
                  onClick={handleClick}
                  content="Previous"
                  className="c-pager__page--previous"
                />
              )}
              {pageLinkNumbers.map(num => {
                const isCurrentPage = num === currentPage;
                const title = isCurrentPage ? "Current Page" : `Page ${num}`;

                return (
                  <PageLink
                    key={`page-${num}`}
                    pageNumber={num}
                    title={title}
                    onClick={handleClick}
                    className={isCurrentPage ? "c-pager__page--current" : ""}
                    content={num}
                  />
                );
              })}
              {hasNext && (
                <PageLink
                  title="Next Page"
                  pageNumber={currentPage + 1}
                  onClick={handleClick}
                  content="Next"
                  className="c-pager__page--next"
                />
              )}
            </ol>
          </div>
        </>
      ) : (
        <p className="c-pager__amount" data-testid="no-page-item-amount">
          <strong>{total} Item(s)</strong>
        </p>
      )}
    </div>
  );
};
